import React, { Component, Fragment } from "react";
import * as s from "./BottomBar.module.scss";
import { Icons } from "src/libs/assets";
import { inject, observer } from "mobx-react";
import { SectionsContainerStore, UIStore } from "src/stores";
import { P, Overlay, ModalToolbar, Button } from "src/views/shared/components";

interface IBottomBarItemTypeData {
  title: string;
  icon: string;
}

export enum BottomBarItemType {
  Share,
  CopyLink,
  Reference,
  SaveToLibrary,
  AdvancedSearch,
  RefineResults,
  DownloadCollection,
  EditCollection,
}

export interface IActionButton {
  title: string;
  onClick?: () => void;
}

export interface IBottomBarItem {
  type: BottomBarItemType;
  component?: any;
  onClick?: any;
  active?: boolean;
  positiveButton?: IActionButton;
  negativeButton?: IActionButton;
}

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  bottomBarItems: IBottomBarItem[];
  uiStore?: UIStore;
  sectionsContainerStore?: SectionsContainerStore;
}

interface State {
  selectedBottomItem?: IBottomBarItem;
}

@inject("uiStore", "sectionsContainerStore")
@observer
export class BottomBar extends Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      selectedBottomItem: null,
    };
  }

  getBottomBarItemTypeData = (
    bottomBarItem: IBottomBarItem
  ): IBottomBarItemTypeData => {
    switch (bottomBarItem.type) {
      case BottomBarItemType.Share:
        return { title: "Share", icon: Icons.share_small };
      case BottomBarItemType.CopyLink:
        return { title: "Copy Link", icon: Icons.link_small };
      case BottomBarItemType.Reference:
        return { title: "Reference", icon: Icons.cite_small };
      case BottomBarItemType.SaveToLibrary:
        return { title: "Library", icon: Icons.library_small };
      case BottomBarItemType.AdvancedSearch:
        return { title: "Advanced search", icon: Icons.settings_small };
      case BottomBarItemType.RefineResults:
        return {
          title: "Refine results",
          icon: bottomBarItem.active
            ? Icons.filter_small_active
            : Icons.filter_small,
        };
      case BottomBarItemType.DownloadCollection:
        return { title: "Download collection", icon: Icons.download_small };
      case BottomBarItemType.EditCollection:
        return { title: "Edit collection", icon: Icons.edit_small };
      default:
        return { title: "", icon: "" };
    }
  };

  togglePromptModal = (bottomBarItem) => {
    this.setState({
      selectedBottomItem:
        this.state.selectedBottomItem == bottomBarItem ? null : bottomBarItem,
    });
  };

  onDoneClick = (bottomBarItem: IBottomBarItem) => {
    bottomBarItem.positiveButton?.onClick &&
      bottomBarItem.positiveButton?.onClick();
    this.togglePromptModal(bottomBarItem);
  };

  onItemClick = (bottomBarItem: IBottomBarItem) => {
    bottomBarItem.onClick
      ? bottomBarItem.onClick()
      : this.togglePromptModal(bottomBarItem);
  };

  renderBottomBarItem = (bottomBarItem: IBottomBarItem, key, array) => {
    const isOneItem = array?.length === 1;
    const bottomBarItemTypeData = this.getBottomBarItemTypeData(bottomBarItem);

    let wrapperClasses = [s.bottomItemIconWrapper];
    let titleClasses = [s.bottomItemTitle];

    if (isOneItem) {
      titleClasses.push(s.largeTitle, s.rowLayout);
    }

    return (
      <Fragment key={key}>
        <div
          className={wrapperClasses.join(" ")}
          onClick={() => this.onItemClick(bottomBarItem)}
        >
          <img
            src={bottomBarItemTypeData.icon}
            alt={bottomBarItemTypeData.title}
            className={s.bottomItemIcon}
          />
          <p className={titleClasses.join(" ")}>
            {bottomBarItemTypeData.title}
          </p>
        </div>
      </Fragment>
    );
  };

  renderOverlay = (bottomBarItem: IBottomBarItem) => {
    const bottomBarItemTypeData = this.getBottomBarItemTypeData(bottomBarItem);

    return (
      <Overlay>
        <div className={s.overlayContainer}>
          <ModalToolbar
            title={bottomBarItemTypeData.title}
            onClose={() => this.togglePromptModal(bottomBarItem)}
            button={bottomBarItem.negativeButton}
          />
          {bottomBarItem.component}
        </div>
        {bottomBarItem?.positiveButton && (
          <>
            <div className={s.buttonFade}>
              <Button
                size="large"
                color="red"
                className={s.button}
                onClick={() => this.onDoneClick(bottomBarItem)}
              >
                {bottomBarItem?.positiveButton?.title}
              </Button>
            </div>
          </>
        )}
      </Overlay>
    );
  };

  render() {
    const { bottomBarItems, sectionsContainerStore } = this.props;
    const { hasOpenOverlay } = sectionsContainerStore;
    const { selectedBottomItem } = this.state;

    let bottomBarContainerClasses = [s.bottomBarContainer];
    let fadeClasses = [s.bottomBarFade];

    if (hasOpenOverlay) {
      bottomBarContainerClasses.push(s.hidden);
      fadeClasses.push(s.hidden);
    }

    return (
      <>
        {selectedBottomItem && this.renderOverlay(selectedBottomItem)}
        <div className={bottomBarContainerClasses.join(" ")}>
          {bottomBarItems.map(this.renderBottomBarItem)}
        </div>
        <div className={fadeClasses.join(" ")} />
      </>
    );
  }
}
